/**
 * 기준정보 API 공통 유틸.
 *
 * - 도메인별 함수·훅은 `masterBase.ts`(거래처·장소·품목·구간)와
 *   `masterOps.ts`(차량·배정·단가·월간계획)에 나눠 둔다.
 * - 서버 에러 본문은 `{success:false, error:{code,message,detail,field}}` 형식이므로
 *   `client.ts` 의 `extractErrorMessage` 만으로는 한국어 메시지를 못 꺼낸다.
 *   여기서 그 envelope 를 풀어 코드·필드까지 화면에 넘긴다.
 */
import axios from "axios";
import apiClient, { extractErrorMessage } from "@/api/client";
import type { Paginated } from "@/types/domain";

// ---------------------------------------------------------------------------
// 에러
// ---------------------------------------------------------------------------

/** 서버 공통 에러 응답을 화면이 쓰기 좋은 형태로 편 것 */
export interface ApiError {
  /** 업무 에러 코드 (예: `ASSIGNMENT_PERIOD_OVERLAP`, `PRICE_NOT_FOUND`) */
  code: string | null;
  /** 그대로 노출해도 되는 한국어 문장 */
  message: string;
  field: string | null;
  detail: unknown;
  status: number | null;
}

interface ErrorEnvelope {
  error?: { code?: string; message?: string; detail?: unknown; field?: string | null };
}

export function toApiError(error: unknown, fallback = "요청 처리에 실패했습니다."): ApiError {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as ErrorEnvelope | undefined;
    const inner = body?.error;
    if (inner && typeof inner.message === "string") {
      return {
        code: inner.code ?? null,
        message: inner.message,
        field: inner.field ?? null,
        detail: inner.detail ?? null,
        status: error.response?.status ?? null,
      };
    }
    return {
      code: null,
      message: extractErrorMessage(error, fallback),
      field: null,
      detail: null,
      status: error.response?.status ?? null,
    };
  }
  return {
    code: null,
    message: extractErrorMessage(error, fallback),
    field: null,
    detail: null,
    status: null,
  };
}

/** 실패 메시지만 화면에 띄우면 되는 경우의 축약형 */
export function apiErrorMessage(error: unknown, fallback?: string): string {
  return toApiError(error, fallback).message;
}

// ---------------------------------------------------------------------------
// 쿼리 파라미터
// ---------------------------------------------------------------------------

export type QueryValue = string | number | boolean | null | undefined;

/** `undefined`·`null`·빈 문자열을 떨어내 쿼리스트링을 깔끔하게 만든다 */
export function cleanParams(
  params: Record<string, QueryValue>
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    out[key] = value;
  });
  return out;
}

// ---------------------------------------------------------------------------
// 숫자 정규화
// ---------------------------------------------------------------------------

/**
 * 백엔드 `Decimal` 필드는 JSON 에 **문자열**로 실려 온다(pydantic v2 기본 동작).
 * 톤수·거리 등을 표에서 계산·정렬하려면 number 로 맞춰 둬야 한다.
 */
export function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const num = typeof value === "number" ? value : Number(value);
  return Number.isNaN(num) ? null : num;
}

/** 지정한 키들만 number 로 정규화한 얕은 복사본을 만든다 */
export function normalizeNumeric<T extends object>(row: T, keys: (keyof T)[]): T {
  const next = { ...row };
  keys.forEach((key) => {
    (next as Record<string, unknown>)[key as string] = toNumber(
      (row as Record<string, unknown>)[key as string]
    );
  });
  return next;
}

// ---------------------------------------------------------------------------
// 조회 헬퍼
// ---------------------------------------------------------------------------

/** 페이지 응답을 그대로 받는다 */
export async function getPaged<T>(
  url: string,
  params: Record<string, QueryValue> = {}
): Promise<Paginated<T>> {
  const res = await apiClient.get<Paginated<T>>(url, { params: cleanParams(params) });
  return res.data;
}

/**
 * 드롭다운용 전체 목록. 백엔드 `size` 상한이 500 이라
 * 장소(631건)·구간(694건)처럼 상한을 넘는 목록은 페이지를 이어 받아야 한다.
 */
export async function fetchAllPages<T>(
  url: string,
  params: Record<string, QueryValue> = {},
  pageSize = 500
): Promise<T[]> {
  const first = await getPaged<T>(url, { ...params, page: 1, size: pageSize });
  const acc = [...first.items];
  const pages = Math.ceil((first.total || 0) / pageSize);
  for (let page = 2; page <= pages; page += 1) {
    const next = await getPaged<T>(url, { ...params, page, size: pageSize });
    acc.push(...next.items);
  }
  return acc;
}

/** 기준정보 쿼리 키 루트 — 도메인 단위 invalidate 에 쓴다 */
export const MASTER_KEY = "master" as const;

export const masterKey = {
  companies: [MASTER_KEY, "companies"] as const,
  locations: [MASTER_KEY, "locations"] as const,
  items: [MASTER_KEY, "items"] as const,
  itemMappings: [MASTER_KEY, "item-mappings"] as const,
  subtotalCategories: [MASTER_KEY, "subtotal-categories"] as const,
  routes: [MASTER_KEY, "routes"] as const,
  vehicles: [MASTER_KEY, "vehicles"] as const,
  assignments: [MASTER_KEY, "vehicle-assignments"] as const,
  /**
   * 단가에서 파생되는 모든 조회의 지붕.
   *
   * 단가를 만들면 매출·매입 목록만이 아니라 **구간별 후보 조회**(거래처·품목)까지 함께
   * 낡는다. 하나씩 무효화하면 새로 생긴 조회를 빠뜨리게 되는데, 빠뜨린 것은 조용히
   * 낡은 값을 보여 준다 — 빠른 등록으로 단가를 넣었는데 `단가 없음` 버튼이 그대로 남아
   * 있던 것이 그 사고다(2026-09-10).
   */
  prices: [MASTER_KEY, "prices"] as const,
  salesPrices: [MASTER_KEY, "prices", "sales"] as const,
  purchasePrices: [MASTER_KEY, "prices", "purchase"] as const,
  plans: [MASTER_KEY, "plans"] as const,
  drivers: [MASTER_KEY, "drivers"] as const,
};

export { apiClient };
